import {
  PriceSourceName,
  StrategyDashboardConsumptionProjectionsData,
} from "src/store/api/generatedApi";

export interface ConsumptionProjectionChartData {
  material_name: string;
  projectionFraction: number;
  projectionMass: number;
  trajectoryFraction: number;
  trajectoryMass: number;
}

interface MaterialProjectionAccumulator {
  projectionFraction?: number;
  projectionMass?: number;
  trajectoryFraction?: number;
  trajectoryMass?: number;
}

export const getConsumptionProjectionChartData = (
  data: StrategyDashboardConsumptionProjectionsData | undefined
): ConsumptionProjectionChartData[] => {
  if (!data?.values) return [];

  const groupedByMaterial = data.values.reduce<
    Record<string, MaterialProjectionAccumulator>
  >((acc, item) => {
    const materialName = item.material_name;
    acc[materialName] ??= {};

    const massFraction =
      item.quantities.find((q) => q.unit === "mass_fraction")?.value ?? 0;
    const mass = item.quantities.find((q) => q.unit === "mass")?.value ?? 0;

    if (item.source === "projectionSource") {
      acc[materialName].projectionFraction = massFraction;
      acc[materialName].projectionMass = mass;
    } else if (item.source === "trajectorySource") {
      acc[materialName].trajectoryFraction = massFraction;
      acc[materialName].trajectoryMass = mass;
    }

    return acc;
  }, {});

  return Object.entries(groupedByMaterial)
    .map(([material_name, values]) => ({
      material_name,
      projectionFraction: values.projectionFraction ?? 0,
      projectionMass: values.projectionMass ?? 0,
      trajectoryFraction: values.trajectoryFraction ?? 0,
      trajectoryMass: values.trajectoryMass ?? 0,
    }))
    .filter((d) => d.projectionFraction > 0 || d.trajectoryFraction > 0);
};

export const getAvailablePriceSources = (
  data: StrategyDashboardConsumptionProjectionsData | undefined
): PriceSourceName[] => {
  if (!data?.values) return [];
  const sources = new Set<PriceSourceName>();
  for (const value of data.values) {
    for (const price of value.prices) {
      sources.add(price.price_source);
    }
  }
  const sorted = [...sources].sort();
  const preferredIdx = sorted.indexOf("inventoryReportSource");
  if (preferredIdx > 0) {
    sorted.splice(preferredIdx, 1);
    sorted.unshift("inventoryReportSource");
  }
  return sorted;
};

export const getWeightedPrice = (
  data: StrategyDashboardConsumptionProjectionsData | undefined,
  consumptionSource: string,
  priceSource: PriceSourceName
): number | null => {
  if (!data?.values) return null;
  const sourceValues = data.values.filter(
    (v) => v.source === consumptionSource
  );
  if (sourceValues.length === 0) return null;

  let total = 0;
  let hasPriceData = false;
  for (const materialEntry of sourceValues) {
    const fraction =
      materialEntry.quantities.find((q) => q.unit === "mass_fraction")?.value ??
      0;
    const priceEntry = materialEntry.prices.find(
      (p) => p.price_source === priceSource
    );
    if (priceEntry !== undefined) {
      hasPriceData = true;
      total += fraction * priceEntry.specific_price;
    }
  }
  return hasPriceData ? total : null;
};
